import React, { useState } from "react";
import { Bookmark, Shuffle as ShuffleIcon, Sparkles } from "lucide-react";
import { SITEDETAIL } from "@/constants/testIds";
import PresetsView from "./PresetsView";
import { TABS } from "./theme_picker/catalog";
import { useApplyTile, useShuffle } from "./theme_picker/useThemePicker";
import { Flash, PREVIEW_W, ThemeTile } from "./theme_picker/ThemeTile";
import { ShuffleView } from "./theme_picker/ShuffleView";

/**
 * Theme Picker — live "shop-by-preview" interface that swaps the site's
 * header / footer / layout / palette / font. Each tile renders the site's REAL
 * content through the admin-only `?preview_*=` SSR query params, so the
 * operator sees exactly how their headlines look under each theme before
 * committing. Shares the preview infrastructure with /admin/template-gallery.
 */

function TabBar({ tab, setTab }) {
  return (
    <div className="flex border-b border-zinc-800 flex-wrap">
      {TABS.map((t) => (
        <button key={t.id} data-testid={SITEDETAIL.themePickerTab(t.id)} onClick={() => setTab(t.id)}
          className={`px-5 py-2.5 text-xs font-semibold uppercase tracking-wider transition inline-flex items-center gap-1.5 ${
            tab === t.id ? "text-amber-300 border-b-2 border-amber-400" : "text-zinc-400 hover:text-zinc-200 border-b-2 border-transparent"}`}>
          {t.kind === "shuffle" && <ShuffleIcon className="w-3 h-3" />}
          {t.kind === "presets" && <Bookmark className="w-3 h-3" />}
          {t.label}
          {t.items.length > 0 && <span className="opacity-50 ml-1">({t.items.length})</span>}
        </button>
      ))}
    </div>
  );
}

function TileGrid({ site, tabDef, bust, busy, flash, onApply }) {
  return (
    <div className="p-5">
      <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${PREVIEW_W + 32}px, 1fr))` }}>
        {tabDef.items.map((item) => (
          <ThemeTile key={item.value ?? `${item.template}/${item.variant}`} site={site} kind={tabDef.kind} item={item} bust={bust} busy={busy} onApply={onApply} />
        ))}
      </div>
      <Flash flash={flash} />
    </div>
  );
}

export default function ThemePickerPanel({ site, reload }) {
  const [tab, setTab] = useState("header");
  const [busy, setBusy] = useState(null); // `${kind}:${key}` while applying
  const [bust, setBust] = useState(0);
  const [flash, setFlash] = useState(null);
  const ctl = { setBust, setFlash, busy, setBusy };
  const shuffle = useShuffle(site, reload, ctl);
  const applyTile = useApplyTile(site, reload, ctl);
  const tabDef = TABS.find((t) => t.id === tab);

  return (
    <div className="border border-zinc-800 rounded-sm bg-zinc-900/30">
      <div className="px-5 pt-5 pb-3 border-b border-zinc-800">
        <div className="flex items-baseline justify-between gap-3 mb-1">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <div className="font-display text-lg font-bold">Theme Picker</div>
          </div>
          <div className="text-[11px] text-zinc-500 mono">live previews of your actual content</div>
        </div>
        <p className="text-xs text-zinc-500 mt-1">
          Pick a header, footer, or layout — each tile renders your site against that variant. Click <span className="text-zinc-300">Apply</span> to make it the default.
        </p>
      </div>
      <TabBar tab={tab} setTab={setTab} />
      {tab === "shuffle" && <ShuffleView s={shuffle} bust={bust} busy={busy} flash={flash} />}
      {tab === "presets" && <PresetsView site={site} reload={reload} />}
      {tab !== "shuffle" && tab !== "presets" && <TileGrid site={site} tabDef={tabDef} bust={bust} busy={busy} flash={flash} onApply={applyTile} />}
    </div>
  );
}
